"use client";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import { Suspense, useState, type PropsWithChildren } from "react";
import { register } from "swiper/element/bundle";

import Skeleton from "@/components/atoms/Skeleton";
import Video from "@/components/atoms/Video";
import AspectRatioProvider from "@/components/modifiers/AspectRatioProvider";

import styles from "./media-gallery.module.css";
import SwiperGalleryNoSSR from "./SwiperGalleryNoSSR";
register();

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "swiper-container": any;
      "swiper-slide": any;
    }
  }
}

type SwiperMedia =
  | {
      href: string;
      alt: string;
      type: string;
    }[]
  | undefined;

const ImagePreviewModal = ({
  children,
  media,
  activeIndex,
  setActiveIndex,
}: PropsWithChildren<{
  media: SwiperMedia;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
}>) => {
  return (
    <Dialog.Root
      open={activeIndex !== null}
      onOpenChange={(open) => {
        if (!open) setActiveIndex(null);
      }}
    >
      {children}
      <Dialog.Overlay
        className={clsx(styles["dialog-overlay"], "dialog-overlay")}
      />
      <Dialog.Content
        className={clsx(styles["media-content"], "dialog-content-fade-in")}
      >
        <Dialog.Close className={styles["close-button"]}>X</Dialog.Close>
        <Suspense>
          <SwiperGalleryNoSSR media={media} activeIndex={activeIndex} inModal />
        </Suspense>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const MySwiper = ({ media }: { media: SwiperMedia }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <ImagePreviewModal
      media={media}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
    >
      <div className={styles.gallery__container}>
        <div className={styles.gallery__container__inner}>
          <div className={styles.gallery__swiper}>
            <SwiperGalleryNoSSR
              media={media}
              activeIndex={activeIndex}
              className={styles.swiper}
            />
          </div>
          <div className={styles.image__gallery}>
            {media?.slice(0, 7).map((image, i) => (
              <AspectRatioProvider
                height={3}
                width={2}
                key={`${image.href}-${i}`}
                onClick={() => {
                  setActiveIndex(i);
                }}
              >
                <div>
                  {media?.length > 7 && i === 6 ? (
                    <div className={styles.image__overlay}>
                      <div className={styles.image__overlay__text}>
                        <p>{media?.length - 7} </p>
                        <p>+</p>
                      </div>
                    </div>
                  ) : null}
                </div>
                {image.type === "image" ? (
                  <div className={styles.media__wrapper}>
                    <Image
                      src={image?.href}
                      alt=""
                      fill
                      quality={80}
                      sizes={`(max-width: 1024px) 100vw, (max-width: 2560px) 60vw`}
                      className={styles.image}
                      priority={i === 0}
                    />
                    <Skeleton height="100%" />
                  </div>
                ) : (
                  <div className={styles.video}>
                    <Suspense fallback={<Skeleton height="100%" />}>
                      <Video
                        url={image?.href}
                        playing
                        loop
                        muted
                        width="100%"
                        height="auto"
                      />
                    </Suspense>
                  </div>
                )}
              </AspectRatioProvider>
            ))}
          </div>
        </div>
      </div>
    </ImagePreviewModal>
  );
};

export default MySwiper;
